import { TreeNode } from "@/types/tree";
import { v4 as uuidV4 } from "uuid";
import { breakdanceUndo } from "@/lib/undo/undo";
import {
  findPlaceToAddNodes,
  getNestingRuleFromNode
} from "@/lib/tree/nesting";
import { documentStore, uiStore } from "@/store/store-accessor";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import { readFromLocalStorage, writeToLocalStorage } from "@/util/localstorage";

export const pasteElements = (
  elements: TreeNode[],
  insertingFromLibraryOrPasting: "inserting" | "pasting" = "inserting"
) => {
  const uniqueId = uuidV4();
  const transactionKey = `pasteElements:${uniqueId}`;

  breakdanceUndo.transaction(
    () => {
      elements.forEach(elementToPaste => {
        const nodeToAddId =
          uiStore.activeElementId ||
          uiStore.inlineEditingContext?.elementId ||
          null;

        const placeToAdd = findPlaceToAddNodes(
          documentStore.document.tree,
          nodeToAddId,
          getNestingRuleFromNode(elementToPaste)
        );

        pipe(
          placeToAdd,
          fold(
            () => {},
            legalPlaceToAdd => {
              if (legalPlaceToAdd === "TOP_LEVEL") return;

              const pointerForCreatedNodeId: { nodeId?: TreeNode["id"] } = {
                nodeId: undefined
              };

              documentStore.pasteCopiedElementMutation({
                element: elementToPaste,
                placeToAddId: legalPlaceToAdd.place.id,
                placeToAddIndex:
                  legalPlaceToAdd.index ||
                  legalPlaceToAdd.place.children.length,
                pointer: pointerForCreatedNodeId
              });

              if (pointerForCreatedNodeId.nodeId) {
                breakdanceStoreActions.run.onCreatedElement({
                  elementId: pointerForCreatedNodeId.nodeId,
                  transactionKey
                });
              }
            }
          )
        );
      });
    },
    insertingFromLibraryOrPasting === "inserting"
      ? `Import from design library`
      : "Paste Elements",
    transactionKey
  );
};

export function getLastUsedDesignProviderUrl(): string | false {
  const maybeProviderUrl = readFromLocalStorage(
    "breakdance_last_used_design_provider"
  );
  if (typeof maybeProviderUrl === "string") {
    return maybeProviderUrl;
  }

  return false;
}

export function setLastUsedDesignProviderUrl(providerUrl: string) {
  writeToLocalStorage("breakdance_last_used_design_provider", providerUrl);
}
